import { SxProps, Theme } from '@mui/material';
import { GLOBAL_MARGIN_HORIZONTAL } from 'src/app/constants';

export const chatShell: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
  gap: { xs: 2, md: 3 },
  height: 'calc(100vh - 120px)',
  paddingTop: GLOBAL_MARGIN_HORIZONTAL,
};

export const sidebarPaper: SxProps<Theme> = {
  borderRadius: 4,
  border: theme => `1px solid ${theme.palette.divider}`,
  boxShadow: '0px 10px 30px rgba(0,0,0,0.08)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  bgcolor: 'background.paper',
};

export const sidebarHeader: SxProps<Theme> = {
  padding: 2,
  borderBottom: theme => `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

export const newChatButton: SxProps<Theme> = {
  textTransform: 'none',
  borderRadius: 3,
  boxShadow: 'none',
};

export const conversationList: SxProps<Theme> = {
  flex: 1,
  overflowY: 'auto',
  padding: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

export const conversationItem = (active?: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  padding: 1.25,
  borderRadius: 3,
  bgcolor: active ? 'action.selected' : 'transparent',
  border: theme =>
    active
      ? `1px solid ${theme.palette.divider}`
      : `1px dashed ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    bgcolor: active ? 'action.selected' : 'action.hover',
    '.convo-actions': { opacity: 1, pointerEvents: 'auto' },
  },
});

export const chatArea: SxProps<Theme> = {
  borderRadius: 4,
  border: theme => `1px solid ${theme.palette.divider}`,
  boxShadow: '0px 10px 30px rgba(0,0,0,0.08)',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'background.paper',
  minHeight: 0,
};

export const chatHeaderBar: SxProps<Theme> = {
  padding: { xs: 2, md: 3 },
  borderBottom: theme => `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
};

export const chatScrollArea: SxProps<Theme> = {
  flexGrow: 1,
  overflowY: 'auto',
  padding: { xs: 2, md: 3 },
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  bgcolor: theme =>
    theme.palette.mode === 'dark'
      ? theme.palette.background.default
      : '#f7f7fb',
};

export const chatMessage = (isUser?: boolean): SxProps<Theme> => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
});

export const chatBubble = (isUser?: boolean): SxProps<Theme> => ({
  maxWidth: '82%',
  p: 2,
  borderRadius: 3,
  border: theme => `1px solid ${theme.palette.divider}`,
  bgcolor: isUser ? '#ffffff' : 'background.paper',
  boxShadow: '0px 10px 30px rgba(0,0,0,0.04)',
});

export const starterCard: SxProps<Theme> = {
  p: { xs: 2, md: 3 },
  borderRadius: 3,
  border: theme => `1px dashed ${theme.palette.divider}`,
  bgcolor: 'background.paper',
  mb: 2,
};

export const promptGrid: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 1.5,
};

export const promptTile = (highlighted?: boolean): SxProps<Theme> => ({
  p: 1.75,
  borderRadius: 2,
  border: theme => `1px solid ${theme.palette.divider}`,
  bgcolor: highlighted ? 'action.hover' : 'background.default',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    bgcolor: 'action.hover',
    transform: 'translateY(-1px)',
  },
});

export const promptChip: SxProps<Theme> = {
  borderRadius: 2,
  border: theme => `1px solid ${theme.palette.divider}`,
};

export const footerNotice: SxProps<Theme> = {
  mt: 1,
  textAlign: 'center',
  color: theme => theme.palette.text.secondary,
};

export const termsLink: SxProps<Theme> = {
  textDecoration: 'underline',
  cursor: 'pointer',
};

export const chatInputWrapper: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 4,
  padding: 2,
  border: theme => `1px solid ${theme.palette.divider}`,
  boxShadow: '0px 10px 30px rgba(0,0,0,0.06)',
  bgcolor: '#ffffff',
  minHeight: 120,
};

export const buttonsWrapper: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  mt: 1,
};

export const inputField: SxProps<Theme> = {
  ml: 1,
  flex: 1,
  fontSize: '1rem',
  '& .MuiInputBase-root': {
    padding: 0,
  },
  '& textarea': {
    border: 'none',
    outline: 'none',
    resize: 'none',
    boxShadow: 'none !important',
    fontFamily: '"Roboto", sans-serif',
  },
};

export const typingDots: SxProps<Theme> = {
  width: 48,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  gap: 0.8,
  '&::before, &::after, span': {
    content: '""',
    display: 'block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: theme => theme.palette.text.secondary,
    animation: 'dotPulse 1.2s infinite ease-in-out',
  },
  '&::before': { animationDelay: '0s' },
  span: { animationDelay: '0.2s' },
  '&::after': { animationDelay: '0.4s' },
  '@keyframes dotPulse': {
    '0%, 100%': { opacity: 0.3, transform: 'scale(0.9)' },
    '50%': { opacity: 1, transform: 'scale(1.1)' },
  },
};
